import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useQueryClient } from "react-query";
import moment from "moment-timezone";
import Delete from "@mui/icons-material/Delete";

import { useFormData } from "../../../hooks/useFormData";
import UserSelect from "./UserSelect";
import ServicesList from "../../common/ServicesList";
import { useApi, useApiGet } from "../../../hooks/useApi";
import { ucwords } from "../../../lib/stringHelpers";
import { toast } from "react-toastify";
import { Divider } from "@mui/material";
import { CostBreakdown } from "./CostBreakdown";

const DEFAULT_VALUES = {
  helper: null,
  customer: null,
  profile: null,
  paymentMethod: null,
  services: {},
  saveDefaultServices: false,
  startDate: moment().add(3, "days").format("YYYY-MM-DD"),
  startTime: "07:00",
  endTime: "10:00",
  hours: 3,
  helperServiceFee: 20,
  customerServiceFee: 20,
  helperRate: null,
  additionalNotes: "",
  _methods: [],
};

const VEHICLE_SERVICES = [
  "60c3fd06573f8b1868c89ef2",
  "6286461e68f0c5cd07ad833f",
  "6286461e68f0c5cd07ad8338",
];

const CreateBookingSidebar = ({ toggle, defaultDate = null }) => {
  const { formData, onChange, setData } = useFormData({ ...DEFAULT_VALUES });
  const [additionalDates, setAdditionalDates] = useState([]);
  const queryClient = useQueryClient();

  // Preload the services data for the <ServicesList /> component
  useApiGet(
    "services",
    "/services",
    null,
    {
      staleTime: 300000,
    }
  );

  const { call: callApi, isLoading } = useApi({
    onError: (err) => toast.error(err.message),
  });

  const getUser = (userId) => {
    callApi(`/admin/users/${userId}`, "get", undefined, undefined, (res) => {
      setData({ _methods: res._paymentMethods }, true);
    });
  };

  const createJob = (payload) => {
    callApi(`/admin/job-requests`, "post", undefined, payload, (res) => {
      toggle();
      toast.success("Your job request has been successfully created.");
      queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
    });
  };

  useEffect(() => {
    if (defaultDate) {
      setData({ ...DEFAULT_VALUES, startDate: defaultDate });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDate]);

  useEffect(() => {
    if (formData.customer) {
      getUser(formData.customer.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customer]);

    useEffect(() => {
    if (formData.helper) {
      setData({
        helperRate: formData.helper.record.helper.hourlyRates.standard
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.helper]);

  const onTimeChange = (e, index = null) => {
    const isStart = e.target.name === "startTime";
    // pick either formData.dates[0] or additionalDates[index]
    const item = index === null ? formData : additionalDates[index];

    let startDt = moment(
      `${item.startDate} ${item.startTime}`,
      "YYYY-MM-DD HH:mm"
    );
    let endDt = moment(
      `${item.startDate} ${item.endTime}`,
      "YYYY-MM-DD HH:mm"
    );
    let updates = {};

    if (isStart) {
      // we want to move the end time forward to maintain the hour difference
      startDt = moment(
        `${startDt.format("YYYY-MM-DD")} ${e.target.value}`,
        "YYYY-MM-DD HH:mm"
      );
      endDt = moment(startDt).add(item.hours, "hours");

      updates = {
        startTime: startDt.format("HH:mm"),
        endTime: endDt.format("HH:mm"),
      };
    } else {
      // we want to calculate the new hour difference between start and end
      startDt = moment(startDt);
      endDt = moment(
        `${endDt.format("YYYY-MM-DD")} ${e.target.value}`,
        "YYYY-MM-DD HH:mm"
      );
      const mins = endDt.diff(startDt, "minutes");

      updates = {
        startTime: startDt.format("HH:mm"),
        endTime: endDt.format("HH:mm"),
        hours: mins / 60,
      };
    }

    if (index === null) {
      setData({ ...updates }, true);
    } else {
      const updatedItem = { ...item, ...updates };
      const copy = [...additionalDates];
      copy[index] = updatedItem;
      setAdditionalDates(copy);
    }
  };

const onHourChange = (e, index = null) => {
    const newHours = e.target.value;

    if (index === null) {
        let startDt = moment(
          `${formData.startDate} ${formData.startTime}`,
          "YYYY-MM-DD HH:mm"
        );
        let endDt = moment(startDt).add(newHours, "hours");

        setData({
          endTime: endDt.format("HH:mm"),
          hours: newHours
        });
    } else {
        const item = additionalDates[index];

        let startDt = moment(
          `${item.startDate} ${item.startTime}`,
          "YYYY-MM-DD HH:mm"
        );
        let endDt = moment(startDt).add(newHours, "hours");

        const updatedItem = {
          ...item,
          endTime: endDt.format("HH:mm"),
          hours: newHours
        };

        const updatedAdditionalDates = [...additionalDates];
        updatedAdditionalDates[index] = updatedItem;

        setAdditionalDates(updatedAdditionalDates);
    }
};

const duplicateDates = () => {
  const all = [
    { date: formData.startDate, time: formData.startTime },
    ...additionalDates.map(d => ({ date: d.startDate, time: d.startTime })),
  ];

  const seen = new Set();
  return all.some(({ date, time }) => {
    const key = `${date}|${time}`;
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
};

const onSubmit = (e) => {
  e.preventDefault();

  // Build the base payload for the original date
  const startDt = moment(
    `${formData.startDate} ${formData.startTime}`,
    "YYYY-MM-DD HH:mm"
  );

  let vehicleRequired = false;
  const servicesRequested = Object.keys(formData.services).map((serviceId) => {
    if (VEHICLE_SERVICES.includes(serviceId)) {
      vehicleRequired = true;
    }
    return {
      service: serviceId,
      options: Object.keys(formData.services[serviceId]).map((option) => ({
        key: option,
        value: formData.services[serviceId][option],
      })),
    };
  });

  const basePayload = { 
    customer: formData.customer.value,
    serviceArea: formData.customer.record.customer.serviceArea,
    helper: null,
    requestFor: formData.profile,
    requestedHelpers: [formData.helper.value],
    helperRate: formData.helperRate,
    jobType: "One-Time",
    declinedBy: [],
    servicesRequested,
    dates: {
      startDateTime: startDt.format(),
      endTime: moment(startDt).add(formData.hours, "hours").format("HH:mm"),
      schedule: null,
      endDate: moment(startDt).add(formData.hours, "hours").format("YYYY-MM-DD"),
    },
    specializedCare: [],
    vehicleRequired,
    hours: formData.hours,
    isOvernight: false,
    isRecurring: false,
    viewedBy: [],
    paymentMethod: formData.paymentMethod,
    acceptedOn: null,
    canceledOn: null,
    additionalNotes: formData.additionalNotes || null,
    serviceFee: {
      helper: Number(formData.helperServiceFee) * 0.01,
      customer: Number(formData.customerServiceFee) * 0.01,
    },
    saveDefaultServices: formData.saveDefaultServices,
  };

  // Submit booking for the main date
  createJob(basePayload);

  // Submit a booking for each additional date
  additionalDates.forEach((date) => {

    const startDt = moment(
      `${date.startDate} ${date.startTime}`,
      "YYYY-MM-DD HH:mm"
    )

    const payloadForDate = {
      ...basePayload,
      hours: date.hours,
      dates: {
        startDateTime: startDt.format(),
        endTime: moment(startDt).add(date.hours, "hours").format("HH:mm"),
        schedule: null,
        endDate: moment(startDt).add(date.hours, "hours").format("YYYY-MM-DD"),
      },
    };

    createJob(payloadForDate);
  });
};

  // set the default services for the selected customer profile
  const [selectedCustomerProfile, setSelectedCustomerProfile] = useState(null);
  useEffect(() => {
    const selectedCustomerProfile = formData.customer?.record.customer.profiles.find(p => p._id === formData.profile) || null;
    setSelectedCustomerProfile(selectedCustomerProfile);

    const services = (selectedCustomerProfile?.defaultServices || []).reduce((services, service) => {
      services[service.service] = {};
      for (const option of service.options) {
        services[service.service][option.key] = option.value;
      }
      return services;
    }, {});

    setData({
      services: services,
      saveDefaultServices: !!selectedCustomerProfile?.defaultServices,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customer, formData.profile, setSelectedCustomerProfile]);

  return (
    <>
      <h3>Create A New Booking</h3>
      <p>
        Creating a booking here will ignore all helper-specific search criteria
        such as availability or service preferences.
      </p>
      <hr />
      <form onSubmit={onSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Helper</Form.Label>
          <UserSelect
            value={formData.helper}
            onChange={(val) => onChange({ target: { name: "helper", value: val } })}
            userType="helper"
            showDefaultOptions={true}
            params={{ clearOnly: "1" }}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Customer</Form.Label>
          <UserSelect
            value={formData.customer}
            onChange={(val) => {
              setData(
                {
                  customer: val,
                  profile: null,
                  paymentMethod: null,
                },
                true
              );
            }}
            userType="customer"
            showDefaultOptions={true}
          />
        </Form.Group>

        <div className={formData.customer ? "" : "d-none"}>
          <Form.Group className={formData.customer ? `mb-3` : "mb-3 d-none"}>
            <Form.Label>Customer Profile</Form.Label>
            <Form.Select
              name="profile"
              value={formData.profile}
              onChange={onChange}
              required={true}
            >
              <option value="">Select a profile</option>
              {formData.customer
                ? formData.customer.record.customer.profiles.map((p, idx) => {
                    return (
                      <option key={idx} value={p._id}>
                        {p.name}
                      </option>
                    );
                  })
                : null}
            </Form.Select>
          </Form.Group>

          <Form.Group className={formData.customer ? `mb-3` : "mb-3 d-none"}>
            <Form.Label>Payment Methods</Form.Label>
            <Form.Select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={onChange}
              required={true}
            >
              {isLoading ? (
                <option value="">Loading...</option>
              ) : (
                <option value="">Select a payment method</option>
              )}
              {formData._methods.length > 0
                ? formData._methods.map((m, idx) => {
                    return (
                      <option key={idx} value={m.id}>{`${ucwords(
                        m.card.display_brand
                      )} ${m.card.last4}`}</option>
                    );
                  })
                : null}
            </Form.Select>
          </Form.Group>

          
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Helper Service Fee %</Form.Label>
                <Form.Control
                  type="number"
                  step={2.5}
                  min={0}
                  max={35}
                  name="helperServiceFee"
                  value={formData.helperServiceFee}
                  onChange={onChange}
                  required={true}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Customer Service Fee %</Form.Label>
                <Form.Control
                  type="number"
                  step={2.5}
                  min={0}
                  max={35}
                  name="customerServiceFee"
                  value={formData.customerServiceFee}
                  onChange={onChange}
                  required={true}
                />
              </Form.Group>
            </Col>
          </Row>

          {formData.helper &&
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Helper Rate</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    name="helperRate"
                    value={formData.helperRate}
                    onChange={onChange}
                    required={true}
                  />
                </Form.Group>
              </Col>
            </Row>
          }

          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={onChange}
                  required={true}
                  format="MM/DD/YYYY"
                  min={moment().format("YYYY-MM-DD")}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Hours</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={24}
                  step={0.25}
                  name="hours"
                  value={formData.hours}
                  onChange={onHourChange}
                  required={true}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Start Time</Form.Label>
                <Form.Control
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={onTimeChange}
                  required={true}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>End Time</Form.Label>
                <Form.Control
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={onTimeChange}
                  required={true}
                />
              </Form.Group>
            </Col>
          </Row>

          <CostBreakdown
            helperRate={formData.helperRate}
            helperServiceFee={formData.helperServiceFee}
            customerServiceFee={formData.customerServiceFee}
            hours={formData.hours}
          />

          <Row>
            <Col className="mb-3">
              {additionalDates.length === 0 &&
                  <Button
                    variant="link"
                    onClick={() =>
                      setAdditionalDates([
                        {
                          startDate: moment(formData.startDate, 'YYYY-MM-DD')
                            .add(1, 'days')
                            .format('YYYY-MM-DD'),
                          hours: formData.hours,
                          startTime: formData.startTime,
                          endTime: formData.endTime,
                        },
                      ])
                    }
                  >
                    Add Another Date
                  </Button>
              }
              {additionalDates.map((date, idx) => (
                <div key={idx}>                   
                  <Row>
                    <Col>
                      <Divider className="mt-3 mb-3" />
                    </Col>
                  </Row>
                  <Row className="justify-content-end">
                    <Col xs="auto">
                      <Button
                        variant="link"
                        onClick={() => {
                          const copy = [...additionalDates];
                          copy.splice(idx, 1);
                          setAdditionalDates(copy);
                        }}
                      >
                        <Delete />
                      </Button>
                    </Col>
                  </Row>
                  <Row className="align-items-end mb-3">
                    <Col>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={date.startDate}
                        onChange={(e) => {
                          const copy = [...additionalDates];
                          copy[idx].startDate = e.target.value;
                          setAdditionalDates(copy);
                        }}
                      />
                    </Col>
                    <Col>
                      <Form.Label>Hours</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={24}
                        step={0.25}
                        value={date.hours}
                        onChange={(e) => onHourChange(e, idx)}
                      />
                    </Col>
                  </Row>
                  <Row className="align-items-end mb-3">
                    <Col>
                      <Form.Label>Start Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="startTime"
                        value={date.startTime}
                        onChange={(e) => onTimeChange(e, idx)}
                      />
                    </Col>
                    <Col>
                      <Form.Label>End Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="endTime"
                        value={date.endTime}
                        onChange={(e) => onTimeChange(e, idx)}
                      />
                    </Col>
                  </Row>
                  <CostBreakdown
                    helperRate={formData.helperRate}
                    helperServiceFee={formData.helperServiceFee}
                    customerServiceFee={formData.customerServiceFee}
                    hours={date.hours}
                  />
                </div>
              ))}
              {additionalDates.length > 0 && (
                <Button
                  variant="link"
                  onClick={() =>
                    setAdditionalDates([
                      ...additionalDates,
                      {
                        startDate: moment(additionalDates[additionalDates.length - 1].startDate, 'YYYY-MM-DD')
                          .add(1, 'days')
                          .format('YYYY-MM-DD'),
                        hours: formData.hours,
                        startTime: formData.startTime,
                        endTime: formData.endTime,
                      },
                    ])
                  }
                >
                  Add Another Date
                </Button>
              )}
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Additional Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={onChange}
              style={{ minHeight: 200 }}
            />
          </Form.Group>
        </div>
        {selectedCustomerProfile && (
          <>
            <ServicesList
              role="customer"
              values={formData.services}
              onChange={(vals) => {
                onChange({ target: { name: "services", value: vals } });
              }}
            />
            <div className="mt-3">
              <Form.Group controlId={`service-save`}>
                <Form.Check
                  name="saveDefaultServices"
                  type="checkbox"
                  label={`Save as Default Services for ${selectedCustomerProfile.name}`}
                  checked={formData.saveDefaultServices}
                  onChange={(e) => onChange({ target: { name: "saveDefaultServices", value: e.target.checked } })}
                />
              </Form.Group>
            </div>
          </>
        )}
        <div className="mb-3 mt-3">
          <Button
            type="submit"
            variant="primary"
            disabled={
              !formData.helper ||
              !formData.customer ||
              !formData.profile ||
              !formData.paymentMethod ||
              duplicateDates() ||
              Object.keys(formData.services).length === 0 ||
              isLoading
            }
          >
            Create Booking
          </Button>
          <Button type="button" variant="link" onClick={() => toggle()}>
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
};

export default CreateBookingSidebar;
